import type { ColorRef, HueName } from './color';

/** Occasion kinds: color family + lucide icon name + a decorative emoji (aria-hidden, never the only label). */
export const occasionKind = {
  birthday: { hue: 'berry', icon: 'cake', emoji: '🎂', label: 'Birthday' },
  christmas: { hue: 'mint', icon: 'tree-pine', emoji: '🎄', label: 'Christmas' },
  hanukkah: { hue: 'blue', icon: 'sparkles', emoji: '🕎', label: 'Hanukkah' },
  anniversary: { hue: 'plum', icon: 'heart', emoji: '💝', label: 'Anniversary' },
  graduation: { hue: 'gold', icon: 'graduation-cap', emoji: '🎓', label: 'Graduation' },
  other: { hue: 'orange', icon: 'gift', emoji: '🎁', label: 'Something else' },
} as const satisfies Record<string, { hue: HueName; icon: string; emoji: string; label: string }>;

export type OccasionKind = keyof typeof occasionKind;

export const avatarColors = ['berry', 'blue', 'gold', 'mint', 'plum', 'orange'] as const;
export type AvatarColor = (typeof avatarColors)[number];

type ToneSet = {
  bg: { light: ColorRef; dark: ColorRef };
  fg: { light: ColorRef; dark: ColorRef };
  solid?: { light: ColorRef; dark: ColorRef };
};

/**
 * Per-hue tone steps. Not every supporting hue has every step, so the mapping is explicit.
 * kind: bg = soft fill, fg = readable text on that fill, solid = decorative (never text).
 */
export const kindTones: Record<OccasionKind, ToneSet> = {
  birthday: {
    bg: { light: 'berry.100', dark: 'berry.900' },
    fg: { light: 'berry.800', dark: 'berry.100' },
    solid: { light: 'berry.500', dark: 'berry.400' },
  },
  christmas: {
    bg: { light: 'mint.100', dark: 'mint.900' },
    fg: { light: 'mint.900', dark: 'mint.100' },
    solid: { light: 'mint.500', dark: 'mint.300' },
  },
  hanukkah: {
    bg: { light: 'blue.100', dark: 'blue.900' },
    fg: { light: 'blue.800', dark: 'blue.100' },
    solid: { light: 'blue.500', dark: 'blue.400' },
  },
  anniversary: {
    bg: { light: 'plum.100', dark: 'plum.700' },
    fg: { light: 'plum.700', dark: 'plum.100' },
    solid: { light: 'plum.500', dark: 'plum.300' },
  },
  graduation: {
    bg: { light: 'gold.100', dark: 'gold.900' },
    fg: { light: 'gold.800', dark: 'gold.100' },
    solid: { light: 'gold.400', dark: 'gold.400' },
  },
  other: {
    bg: { light: 'orange.100', dark: 'orange.900' },
    fg: { light: 'orange.900', dark: 'orange.100' },
    solid: { light: 'orange.500', dark: 'orange.300' },
  },
};

export const avatarTones: Record<AvatarColor, ToneSet> = {
  berry: {
    bg: { light: 'berry.100', dark: 'berry.800' },
    fg: { light: 'berry.700', dark: 'berry.100' },
  },
  blue: {
    bg: { light: 'blue.100', dark: 'blue.800' },
    fg: { light: 'blue.700', dark: 'blue.100' },
  },
  gold: {
    bg: { light: 'gold.100', dark: 'gold.800' },
    fg: { light: 'gold.800', dark: 'gold.100' },
  },
  mint: {
    bg: { light: 'mint.100', dark: 'mint.700' },
    fg: { light: 'mint.700', dark: 'mint.100' },
  },
  plum: {
    bg: { light: 'plum.100', dark: 'plum.700' },
    fg: { light: 'plum.700', dark: 'plum.100' },
  },
  orange: {
    bg: { light: 'orange.100', dark: 'orange.700' },
    fg: { light: 'orange.700', dark: 'orange.100' },
  },
};
